import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  BookOpen, Calendar, ArrowUpRight, ArrowDownRight, Search, Filter,
  Download, Loader2, Package, Building2, User, FileText, RefreshCw
} from 'lucide-react';

const BookLedger = () => {
  const [transactions, setTransactions] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [transRes, prodRes] = await Promise.all([
        api.getStockTransactions(null, 500),
        api.getStockProducts()
      ]);
      setTransactions(transRes.data || []);
      setProducts(prodRes.data || []);
    } catch (error) {
      console.error('Error fetching ledger data:', error);
      toast.error('Failed to load ledger data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product?.name || productId || 'Unknown';
  };

  const filteredTransactions = transactions.filter(t => {
    // Type filter
    if (typeFilter !== 'all' && t.type !== typeFilter) return false;
    
    // Product filter
    if (productFilter && t.product_id !== productFilter) return false;
    
    // Date filter
    if (dateFilter) {
      const transDate = new Date(t.created_at).toISOString().split('T')[0];
      if (transDate !== dateFilter) return false;
    }
    
    // Search filter
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      const productName = getProductName(t.product_id).toLowerCase();
      const notes = (t.notes || '').toLowerCase();
      const partyName = (t.vendor_name || t.customer_name || t.office_name || '').toLowerCase();
      return productName.includes(search) || notes.includes(search) || partyName.includes(search);
    }
    
    return true;
  });

  // Calculate running balance per product
  const getTransactionsWithBalance = () => {
    const balances = {};
    const result = [];
    
    // Process in chronological order to calculate running balance
    const sorted = [...filteredTransactions].sort((a, b) => 
      new Date(a.created_at) - new Date(b.created_at)
    );
    
    sorted.forEach(t => {
      const prodId = t.product_id;
      if (!balances[prodId]) balances[prodId] = 0;
      
      const qty = parseInt(t.quantity) || 0;
      
      if (t.type === 'inward') {
        balances[prodId] += qty;
      } else {
        balances[prodId] -= qty;
      }
      
      result.push({
        ...t,
        runningBalance: balances[prodId]
      });
    });
    
    return result.reverse(); // Return in reverse chronological order
  };

  const transactionsWithBalance = getTransactionsWithBalance();

  const exportToCSV = () => {
    if (!transactionsWithBalance.length) return;
    
    const headers = ['Date', 'Time', 'Type', 'Product', 'Quantity', 'Party', 'Notes', 'Balance'];
    const rows = transactionsWithBalance.map(t => [
      formatDate(t.created_at),
      formatTime(t.created_at),
      t.type === 'inward' ? 'IN' : 'OUT',
      getProductName(t.product_id),
      t.quantity,
      t.vendor_name || t.customer_name || t.office_name || '-',
      t.notes || '-',
      t.runningBalance
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `book_ledger_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Ledger exported successfully');
  };

  // Calculate summary stats
  const totalInward = filteredTransactions.filter(t => t.type === 'inward').reduce((sum, t) => sum + (parseInt(t.quantity) || 0), 0);
  const totalOutward = filteredTransactions.filter(t => t.type === 'outward').reduce((sum, t) => sum + (parseInt(t.quantity) || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="book-ledger-page" className="max-w-[1600px] mx-auto space-y-4 sm:space-y-6 px-2 sm:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-headings text-slate-900">Book Ledger</h1>
          <p className="text-sm sm:text-base text-slate-600 font-body mt-1">
            View all stock transactions and running balances
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchData}
            className="px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-2 transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw size={18} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            onClick={exportToCSV}
            disabled={!filteredTransactions.length}
            className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            data-testid="export-btn"
          >
            <Download size={18} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 sm:p-3 bg-blue-100 rounded-lg">
              <FileText className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="text-xl sm:text-2xl font-bold text-slate-900">{filteredTransactions.length}</p>
              <p className="text-xs sm:text-sm text-slate-500">Transactions</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 sm:p-3 bg-green-100 rounded-lg">
              <ArrowDownRight className="text-green-600" size={20} />
            </div>
            <div>
              <p className="text-xl sm:text-2xl font-bold text-green-600">+{totalInward}</p>
              <p className="text-xs sm:text-sm text-slate-500">Total Inward</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 col-span-2 sm:col-span-1">
          <div className="flex items-center gap-3">
            <div className="p-2 sm:p-3 bg-red-100 rounded-lg">
              <ArrowUpRight className="text-red-600" size={20} />
            </div>
            <div>
              <p className="text-xl sm:text-2xl font-bold text-red-600">-{totalOutward}</p>
              <p className="text-xs sm:text-sm text-slate-500">Total Outward</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={18} className="text-slate-500" />
          <span className="font-medium text-slate-700">Filters</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search product, party, notes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="search-input"
              />
            </div>
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="type-filter"
          >
            <option value="all">All Types</option>
            <option value="inward">Inward Only</option>
            <option value="outward">Outward Only</option>
          </select>
          <select
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="product-filter"
          >
            <option value="">All Products</option>
            {products.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="date-filter"
          />
        </div>
      </div>

      {/* Ledger Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Product</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Party</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Qty</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Balance</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {transactionsWithBalance.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center text-slate-500">
                    <BookOpen size={48} className="mx-auto mb-3 text-slate-300" />
                    <p className="font-medium">No transactions found</p>
                    <p className="text-sm mt-1">Try adjusting your filters</p>
                  </td>
                </tr>
              ) : (
                transactionsWithBalance.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50" data-testid={`transaction-row-${t.id}`}>
                    <td className="px-4 py-3">
                      <div className="text-sm text-slate-900">{formatDate(t.created_at)}</div>
                      <div className="text-xs text-slate-400">{formatTime(t.created_at)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
                        t.type === 'inward' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {t.type === 'inward' ? (
                          <ArrowDownRight size={12} />
                        ) : (
                          <ArrowUpRight size={12} />
                        )}
                        {t.type === 'inward' ? 'IN' : 'OUT'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Package size={14} className="text-slate-400" />
                        <span className="text-sm font-medium text-slate-900">{getProductName(t.product_id)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {t.vendor_name && <Building2 size={14} className="text-slate-400" />}
                        {t.customer_name && <User size={14} className="text-slate-400" />}
                        {t.office_name && <Building2 size={14} className="text-slate-400" />}
                        <span className="text-sm text-slate-600">
                          {t.vendor_name || t.customer_name || t.office_name || '-'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-sm font-semibold ${
                        t.type === 'inward' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {t.type === 'inward' ? '+' : '-'}{t.quantity}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-medium text-slate-900">{t.runningBalance}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-500 truncate max-w-[150px] block">
                        {t.notes || '-'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Footer */}
        {transactionsWithBalance.length > 0 && (
          <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 text-sm text-slate-500">
            Showing {transactionsWithBalance.length} transactions
          </div>
        )}
      </div>
    </div>
  );
};

export default BookLedger;
